import { useEffect, useMemo } from 'react';
import {
  ModelModal,
  type Model,
  type ModelService,
  type ModelRequest,
} from '@ctzhian/modelkit';
import {
  createModelConfig,
  updateModelConfig,
  checkModelConfig,
  getSupportedModels,
  ModelType,
  ModelConfig,
  UpdateModelReq,
} from '@/services/admin';
import { toast } from '@/ui/toast';

interface AdminModelConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  modelType: ModelType;
  // 编辑模式相关
  editingConfig?: ModelConfig | null;
  onSuccess: () => void;
}

const MODELKIT_ICONFONT_SCRIPT_ID = 'modelkit-iconfont';

function ensureModelkitIconfontLoaded() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(MODELKIT_ICONFONT_SCRIPT_ID)) return;

  const baseUrl = import.meta.env.BASE_URL || '/';
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

  const script = document.createElement('script');
  script.id = MODELKIT_ICONFONT_SCRIPT_ID;
  script.async = true;
  script.src = `${normalizedBaseUrl}modelkit-iconfont.js`;
  document.head.appendChild(script);
}

// 模型类型映射（用于 ModelModal 组件）
const MODEL_TYPE_MAP: Record<ModelType, 'chat' | 'embedding'> = {
  Chat: 'chat',
  Embedding: 'embedding',
};

// 模型类型映射（用于后端 API）
const API_MODEL_TYPE_MAP: Record<ModelType, 'chat' | 'embedding'> = {
  Chat: 'chat',
  Embedding: 'embedding',
};

// 模型类型名称
const MODEL_TYPE_NAMES: Record<ModelType, string> = {
  Chat: 'Chat 模型',
  Embedding: 'Embedding 向量模型',
};

export function AdminModelConfigModal({
  open,
  onOpenChange,
  tenantId,
  modelType,
  editingConfig,
  onSuccess,
}: AdminModelConfigModalProps) {
  useEffect(() => {
    if (open) ensureModelkitIconfontLoaded();
  }, [open]);

  // 将 ModelConfig 转换为 Model
  const modelData: Model | null = useMemo(() => {
    if (!editingConfig) return null;
    return {
      id: editingConfig.id,
      provider: editingConfig.provider,
      base_url: editingConfig.base_url || '',
      api_key: editingConfig.api_key || '', // 回显 API Key，组件内部会处理显示/隐藏
      api_version: editingConfig.api_version || '',
      api_header: editingConfig.api_header || '',
      model_name: editingConfig.model_name,
      show_name: editingConfig.description || '', // 将后端的 description 映射到组件的 show_name（模型备注）
      model_type: MODEL_TYPE_MAP[modelType], // 使用传入的 modelType 而不是 editingConfig.type
      param: editingConfig.parameters
        ? {
            context_window: editingConfig.parameters.context_window,
            max_tokens: editingConfig.parameters.max_tokens,
            r1_enabled: editingConfig.parameters.r1_enabled,
            support_images: editingConfig.parameters.support_images,
          }
        : undefined,
    };
  }, [editingConfig, modelType]);

  const modelService = useMemo<ModelService>(() => {
    return {
      // 创建模型
      createModel: async (req: ModelRequest) => {
        try {
          const apiHeader =
            typeof req.api_header === 'string'
              ? req.api_header
              : req.api_header
                ? JSON.stringify(req.api_header)
                : undefined;
          const result = await createModelConfig(tenantId, {
            type: API_MODEL_TYPE_MAP[modelType],
            provider: req.provider || '',
            model_name: req.model_name || '',
            api_key: req.api_key || '',
            base_url: req.base_url,
            api_version: req.api_version,
            api_header: apiHeader,
            description: req.show_name, // 将组件的 show_name 保存到后端的 description 字段
            is_active: true,
            is_default: false,
            parameters: req.param
              ? {
                  context_window: req.param.context_window,
                  max_tokens: req.param.max_tokens,
                  r1_enabled: req.param.r1_enabled,
                  support_images: req.param.support_images,
                }
              : undefined,
          });
          toast.success(`${MODEL_TYPE_NAMES[modelType]}配置成功`);
          onSuccess();
          onOpenChange(false);
          return {
            model: {
              id: result.id,
              provider: result.provider,
              base_url: result.base_url,
              model_name: result.model_name,
              model_type: MODEL_TYPE_MAP[modelType],
            },
          };
        } catch (error) {
          console.error('创建模型配置失败:', error);
          toast.error('创建模型配置失败');
          throw error;
        }
      },

      // 更新模型
      updateModel: async (req: ModelRequest) => {
        if (!editingConfig) {
          throw new Error('没有要编辑的配置');
        }
        try {
          const apiHeader =
            typeof req.api_header === 'string'
              ? req.api_header
              : req.api_header
                ? JSON.stringify(req.api_header)
                : undefined;
          const updateData: UpdateModelReq = {
            id: editingConfig.id,
            provider: req.provider,
            model_name: req.model_name,
            base_url: req.base_url,
            api_version: req.api_version,
            api_header: apiHeader,
            description: req.show_name, // 将组件的 show_name 保存到后端的 description 字段
            is_active: true,
            is_default: false,
          };
          // 只有输入了新的 API Key 才更新
          if (req.api_key) {
            updateData.api_key = req.api_key;
          }
          if (req.param) {
            updateData.parameters = {
              context_window: req.param.context_window,
              max_tokens: req.param.max_tokens,
              r1_enabled: req.param.r1_enabled,
              support_images: req.param.support_images,
            };
          }
          const result = await updateModelConfig(
            tenantId,
            editingConfig.id,
            updateData
          );
          toast.success(`${MODEL_TYPE_NAMES[modelType]}配置更新成功`);
          onSuccess();
          onOpenChange(false);
          return {
            model: {
              id: result.id,
              provider: result.provider,
              base_url: result.base_url,
              model_name: result.model_name,
              model_type: MODEL_TYPE_MAP[modelType],
            },
          };
        } catch (error) {
          console.error('更新模型配置失败:', error);
          toast.error('更新模型配置失败');
          throw error;
        }
      },

      // 测试模型连通性
      checkModel: async (req: ModelRequest) => {
        try {
          const apiHeader =
            typeof req.api_header === 'string'
              ? req.api_header
              : req.api_header
                ? JSON.stringify(req.api_header)
                : undefined;
          const result = await checkModelConfig({
            provider: req.provider || '',
            model: req.model_name || '',
            base_url: req.base_url || '',
            api_key: req.api_key,
            api_version: req.api_version,
            api_header: apiHeader,
            type: MODEL_TYPE_MAP[modelType],
          });
          if (result.error) {
            throw new Error(result.error);
          }
          return {
            model: {
              provider: req.provider,
              base_url: req.base_url,
              model_name: req.model_name,
              model_type: MODEL_TYPE_MAP[modelType],
            },
          };
        } catch (error) {
          console.error('测试模型连通性失败:', error);
          throw error;
        }
      },

      // 获取支持的模型列表
      listModel: async (req: ModelRequest) => {
        try {
          const apiHeader =
            typeof req.api_header === 'string'
              ? req.api_header
              : req.api_header
                ? JSON.stringify(req.api_header)
                : undefined;
          const requestData = {
            provider: req.provider || '',
            base_url: req.base_url || '',
            api_key: req.api_key,
            api_header: apiHeader,
            type: MODEL_TYPE_MAP[modelType],
          };
          console.log('[listModel] 请求参数:', requestData);
          const result = await getSupportedModels(requestData);
          console.log('[listModel] 响应结果:', result);
          if (result.error) {
            console.warn('获取模型列表失败:', result.error);
            return { models: [] };
          }
          return {
            models: (result.models || []).map((m) => ({ model: m.model })),
          };
        } catch (error) {
          console.error('获取模型列表失败:', error);
          return { models: [] };
        }
      },
    };
  }, [tenantId, modelType, editingConfig, onSuccess, onOpenChange]);

  return (
    <ModelModal
      open={open}
      onClose={() => onOpenChange(false)}
      refresh={() => {}}
      data={modelData}
      model_type={MODEL_TYPE_MAP[modelType]}
      modelService={modelService}
      language="zh-CN"
    />
  );
}
